import { useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLoginMutation } from '@/shared/hooks/api/useAuth';
import { parseApiError, sanitizeLoginFormValues } from '../utils';
import { REDIRECT_URLS } from '../constants/index';
import type { UseLoginState } from '../types';
import type { LoginFormData } from '../schemas';
import { useNotification } from '@/shared/utils/notification';

/**
 * Custom hook for login logic - isolated for easy testing
 * Uses React Query mutation hook for API calls
 * Shows backend errors via toast notification at top-left
 * Redirects to previous location after successful login (if available)
 */
export const useLogin = () => {
  const [state, setState] = useState<UseLoginState>({
    isLoading: false,
    error: null,
    isSuccess: false,
  });

  const navigate = useNavigate();
  const location = useLocation();
  const loginMutation = useLoginMutation();
  const { showToast } = useNotification();

  /**
   * Handle login submission
   */
  const handleLogin = useCallback(
    async (formValues: LoginFormData) => {
      // Check network status BEFORE making API call
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        // User is offline - fail immediately without API call
        const errorMessage =
          'No internet connection. Please check your network and try again.';

        setState({
          isLoading: false,
          error: errorMessage,
          isSuccess: false,
        });

        showToast({
          type: 'error',
          title: 'No Internet',
          message: errorMessage,
        });

        throw new Error(errorMessage);
      }

      // Reset state
      setState({
        isLoading: true,
        error: null,
        isSuccess: false,
      });

      try {
        // Sanitize input values
        const sanitizedValues = sanitizeLoginFormValues(formValues);

        // Call API via React Query mutation
        // Validation is handled in mutationFn, will throw if invalid
        const response = await loginMutation.mutateAsync({
          companyCode: sanitizedValues.companyCode,
          userId: sanitizedValues.userId,
          password: sanitizedValues.password,
        });

        // Success state (response is already validated)
        setState({
          isLoading: false,
          error: null,
          isSuccess: true,
        });

        // Navigate to previous location or default dashboard
        // Get the location where user was redirected from (if any)
        const from =
          (location.state as { from?: string })?.from ||
          REDIRECT_URLS.DEFAULT_AFTER_LOGIN;
        navigate(from, { replace: true });

        // Show success message
        showToast({
          type: 'success',
          title: 'Login Successful',
          message: 'Welcome back! You have successfully logged in.',
        });

        return response;
      } catch (error) {
        // Parse error message from backend or network error
        const errorMessage = parseApiError(error);

        // Extract backend error code if available (CHK001-CHK008)
        const errorCode =
          (error as { code?: string; backendError?: { code?: string } })
            ?.code ||
          (error as { code?: string; backendError?: { code?: string } })
            ?.backendError?.code;

        setState({
          isLoading: false,
          error: errorMessage,
          isSuccess: false,
        });

        // Show error notification with specific backend message
        showToast({
          type: 'error',
          title: errorCode ? `Error ${errorCode}` : 'Login Failed',
          message: errorMessage,
        });

        throw error;
      }
    },
    [loginMutation, navigate, location, showToast],
  );

  /**
   * reset error state
   */
  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  /**
   * Reset all state
   */
  const reset = useCallback(() => {
    setState({
      isLoading: false,
      error: null,
      isSuccess: false,
    });
  }, []);

  return {
    ...state,
    handleLogin,
    clearError,
    reset,
  };
};
